import React, { useEffect, useMemo, useState } from "react";
import "./AdminDashboard.css";
import { adminApi } from "../../utils/adminApi";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

const formatMonth = (date) =>
  date.toLocaleString("en-US", { month: "short", year: "2-digit" });

const escapePdfText = (value = "") =>
  String(value).replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");

const createSimplePdf = (lines) => {
  const safeLines = lines.map((line) => escapePdfText(line));
  const textCommands = safeLines
    .map((line, index) => `BT /F1 11 Tf 50 ${770 - index * 16} Td (${line}) Tj ET`)
    .join("\n");

  const content = `%PDF-1.4
1 0 obj
<< /Type /Catalog /Pages 2 0 R >>
endobj
2 0 obj
<< /Type /Pages /Kids [3 0 R] /Count 1 >>
endobj
3 0 obj
<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>
endobj
4 0 obj
<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>
endobj
5 0 obj
<< /Length ${textCommands.length} >>
stream
${textCommands}
endstream
endobj
xref
0 6
0000000000 65535 f 
0000000010 00000 n 
0000000061 00000 n 
0000000118 00000 n 
0000000244 00000 n 
0000000314 00000 n 
trailer
<< /Root 1 0 R /Size 6 >>
startxref
${384 + textCommands.length}
%%EOF`;

  return new Blob([content], { type: "application/pdf" });
};

const toArray = (value) => (Array.isArray(value) ? value : []);

const AdminDashboard = () => {
  const [brands, setBrands] = useState([]);
  const [services, setServices] = useState([]);
  const [rfqItems, setRfqItems] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setLoadError("");

      const [brandsRes, bookingsRes, servicesRes, rfqRes, techniciansRes] =
        await Promise.allSettled([
          adminApi.get("/api/brands"),
          adminApi.get("/api/bookings"),
          adminApi.get("/api/services"),
          adminApi.get("/api/rfq"),
          adminApi.get("/api/technicians"),
        ]);

      if (brandsRes.status === "fulfilled") {
        setBrands(toArray(brandsRes.value.data));
      } else {
        setBrands([]);
      }

      if (bookingsRes.status === "fulfilled") {
        setBookings(toArray(bookingsRes.value.data));
      } else {
        setBookings([]);
      }

      if (servicesRes.status === "fulfilled") {
        setServices(toArray(servicesRes.value.data));
      } else {
        setServices([]);
      }

      if (rfqRes.status === "fulfilled") {
        setRfqItems(toArray(rfqRes.value.data));
      } else {
        setRfqItems([]);
      }

      if (techniciansRes.status === "fulfilled") {
        setTechnicians(toArray(techniciansRes.value.data));
      } else {
        setTechnicians([]);
      }

      const hasFailedRequest = [
        brandsRes,
        bookingsRes,
        servicesRes,
        rfqRes,
        techniciansRes,
      ].some((result) => result.status === "rejected");

      if (hasFailedRequest) {
        setLoadError("Some dashboard widgets could not be loaded.");
      }

      setLoading(false);
    };

    fetchData();
  }, []);

  const stats = useMemo(() => {
    const totalOrders = bookings.length;
    const totalCustomers = new Set(
      bookings.map((booking) => {
        const phone = String(booking.phone || "").trim();
        const email = String(booking.email || "").trim().toLowerCase();
        return phone || email || String(booking._id || "");
      })
    ).size;
    const pendingRepairs = bookings.filter(
      (booking) => (booking.status || "Pending") === "Pending"
    ).length;
    const completedRepairs = bookings.filter(
      (booking) => booking.status === "Completed"
    ).length;

    return {
      totalOrders,
      totalCustomers,
      totalBrands: brands.length,
      totalServices: services.length,
      totalRfqs: rfqItems.length,
      totalTechnicians: technicians.length,
      pendingRepairs,
      completedRepairs,
    };
  }, [bookings, brands, services, rfqItems, technicians]);

  const chartData = useMemo(() => {
    const now = new Date();
    const monthBuckets = [];

    for (let i = 5; i >= 0; i -= 1) {
      monthBuckets.push(new Date(now.getFullYear(), now.getMonth() - i, 1));
    }

    const labels = monthBuckets.map((monthDate) => formatMonth(monthDate));
    const counts = monthBuckets.map((bucketStart) => {
      const year = bucketStart.getFullYear();
      const month = bucketStart.getMonth();
      return bookings.filter((booking) => {
        const created = new Date(booking.createdAt);
        return created.getFullYear() === year && created.getMonth() === month;
      }).length;
    });

    return {
      labels,
      datasets: [
        {
          label: "Bookings",
          data: counts,
          borderColor: "#ff6b6b",
          backgroundColor: "rgba(255,107,107,0.15)",
          tension: 0.4,
          fill: true,
        },
      ],
    };
  }, [bookings]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
  };

  const downloadReport = () => {
    const generatedAt = new Date();
    const reportLines = [
      "BookMyRepair - Dashboard Report",
      `Generated: ${generatedAt.toLocaleString()}`,
      "",
      "Summary",
      `Total Orders: ${stats.totalOrders}`,
      `Total Customers: ${stats.totalCustomers}`,
      `Pending Repairs: ${stats.pendingRepairs}`,
      `Completed Repairs: ${stats.completedRepairs}`,
      `Total Brands: ${stats.totalBrands}`,
      `Total Services: ${stats.totalServices}`,
      `Total RFQ Requests: ${stats.totalRfqs}`,
      `Total Technicians: ${stats.totalTechnicians}`,
      "",
      "Recent Bookings",
      ...bookings.slice(0, 20).map((booking, index) => {
        const customer = booking.name || booking.customerName || "Unknown";
        const phone = booking.phone || "N/A";
        const brand = booking.brandName || booking.brand || "N/A";
        const model = booking.modelName || booking.model || "N/A";
        const status = booking.status || "Pending";
        return `${index + 1}. ${customer} | ${phone} | ${brand} ${model} | ${status}`;
      }),
    ];

    const blob = createSimplePdf(reportLines);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "dashboard-report.pdf";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div>
          <h1>Dashboard Overview</h1>
          <p>
            {loading
              ? "Loading latest admin data..."
              : `Live data: ${stats.totalOrders} bookings, ${stats.totalCustomers} customers, ${stats.totalBrands} brands.`}
          </p>
          {loadError ? <p>{loadError}</p> : null}
        </div>
        <button className="btn-primary" onClick={downloadReport}>
          Download Report
        </button>
      </div>

      <div className="dashboard-stats-grid">
        <div className="dashboard-stat-card card1">
          <h4>Total Orders</h4>
          <h2>{stats.totalOrders}</h2>
          <span>{stats.completedRepairs} completed</span>
        </div>

        <div className="dashboard-stat-card card2">
          <h4>Total Customers</h4>
          <h2>{stats.totalCustomers}</h2>
          <span>Unique phone/email</span>
        </div>

        <div className="dashboard-stat-card card3">
          <h4>Total Brands</h4>
          <h2>{stats.totalBrands}</h2>
          <span>{stats.totalServices} services configured</span>
        </div>

        <div className="dashboard-stat-card card4">
          <h4>Pending Repairs</h4>
          <h2>{stats.pendingRepairs}</h2>
          <span>{stats.totalOrders === 0 ? "No bookings yet" : "Needs action"}</span>
        </div>
      </div>

      <div className="chart-card">
        <div className="chart-header">
          <h3>Booking Analytics</h3>
          <select disabled>
            <option>Last 6 Months</option>
          </select>
        </div>

        <div className="chart-wrapper">
          <Line data={chartData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
